"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useTheme } from "next-themes";
import { useAuth } from "@/components/providers/auth-context";
import { useNav } from "@/components/providers/nav-context";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { UserNav } from "@/components/layout/user-nav";
import { AdminNav } from "@/components/admin/admin-nav";
import { Menu } from "lucide-react";
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

interface NavbarProps {
  showThemeToggleOnly?: boolean;
}

export function Navbar({ showThemeToggleOnly = false }: NavbarProps) {
  const { userProfile } = useAuth();
  const { hideMainNavItems } = useNav();
  const pathname = usePathname();
  const router = useRouter();
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    if (pathname === "/") {
      // If on home page, just scroll
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      // If on another page, navigate to home page with hash using Next.js router
      router.push(`/#${id}`);
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="mx-auto w-full flex h-16 items-center justify-between px-4">
        <div className="flex items-center gap-2 md:gap-10">
          <Link href="/" className="flex items-center space-x-2">
            <div className="relative h-12 w-32">
              {mounted ? (
                <Image
                  src={resolvedTheme === "dark" ? "/images/logo-full-dark.svg" : "/images/logo-full.svg"}
                  alt="Docs4AI Logo"
                  fill
                  className="object-contain"
                />
              ) : null}
            </div>
          </Link>          {!showThemeToggleOnly && !hideMainNavItems && (
            <nav className="hidden gap-6 md:flex">
              <Link
                href="/#features"
                className="flex items-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                onClick={(e) => scrollToSection(e, "features")}
              >
                Features
              </Link>
              <Link
                href="/chat"
                className="flex items-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                Chat
              </Link>
              {userProfile && (
                <Link
                  href="/memory-dashboard"
                  className="hidden lg:flex items-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  Memory Dashboard
                </Link>
              )}
              <Link
                href="/price"
                className="flex items-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                Pricing
              </Link>
              {/* <Link
                href="/docs"
                className="flex items-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                Docs
              </Link>
              <Link
                href="/api-playground"
                className="flex items-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                API Playground
              </Link> */}
            </nav>
          )}
        </div>
        <div className={`flex items-center gap-4 ${showThemeToggleOnly ? 'ml-auto' : ''}`}>
          <ThemeToggle />
          {!showThemeToggleOnly && (
            userProfile?.email?.endsWith("@genwave.xyz") ? <AdminNav /> :
              userProfile ? <UserNav /> : (
                <>
                  <Link href="/login" className="hidden md:block">
                    <Button variant="ghost" size="sm">
                      Log in
                    </Button>
                  </Link>
                  <Link href="/signup" className="hidden md:block">
                    <Button size="sm">Sign up</Button>
                  </Link>
                </>
              )
          )}
          {/* Mobile Menu - Hide on chat pages and if only showing theme toggle */}
          {!pathname.startsWith('/chat') && !showThemeToggleOnly && (
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px] p-0">
                <SheetHeader className="p-6 border-b">
                  <SheetTitle>Menu</SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col p-6">
                  {!hideMainNavItems ? (
                    <div className="space-y-3">
                      <Link
                        href="/#features"
                        className="block text-sm font-medium text-muted-foreground transition-colors hover:text-foreground hover:bg-muted/50 rounded-md px-3 py-2"
                        onClick={(e) => {
                          scrollToSection(e, "features");
                        }}
                      >
                        Features
                      </Link>
                      <Link
                        href="/chat"
                        className="block text-sm font-medium text-muted-foreground transition-colors hover:text-foreground hover:bg-muted/50 rounded-md px-3 py-2"
                      >
                        Chat
                      </Link>
                      {/* <Link
                        href="/docs"
                        className="block text-sm font-medium text-muted-foreground transition-colors hover:text-foreground hover:bg-muted/50 rounded-md px-3 py-2"
                      >
                        Docs
                      </Link>
                      <Link
                        href="/api-playground"
                        className="block text-sm font-medium text-muted-foreground transition-colors hover:text-foreground hover:bg-muted/50 rounded-md px-3 py-2"
                      >
                        API Playground
                      </Link> */}
                    </div>
                  ) : (
                    <div className="space-y-1"></div>
                  )}
                  {!showThemeToggleOnly && (
                    <div className="mt-6 pt-6 border-t space-y-2">
                      {!userProfile && (
                        <>
                          <Link href="/login" className="block">
                            <Button variant="outline" className="w-full" size="sm">
                              Log in
                            </Button>
                          </Link>
                          <Link href="/signup" className="block">
                            <Button className="w-full" size="sm">
                              Sign up
                            </Button>
                          </Link>
                        </>
                      )}
                    </div>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          )}
        </div>
      </div>
    </header>
  );
}
